import {
    DependencyList,
    PropsWithChildren,
    useCallback,
    useMemo,
    useRef,
} from "react";
import classNames from "classnames";
import { Card } from "@design-system/Card/Card";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { DetailsAsideViewportOffsetContext } from "./DetailsAside.context";
import styles from "./DetailsAside.module.scss";

interface DetailsAsideProps {
    className?: string;
    /**
     * Same contract as ViewTable's `recomputeTopOffsetOn` — force a re-measure
     * when chrome above the aside shifts without resizing the aside itself.
     */
    recomputeTopOffsetOn?: DependencyList;
}

/**
 * Right-hand details panel shell shared by transactions / invoices / bills.
 * Self-measures its viewport top offset (like ViewTable's `fixedTopOffsetPx`)
 * and pins height to `100vh - offset` so scroll + fixed command bars stay
 * aligned with the adjacent table.
 */
export const DetailsAside = ({
    children,
    className,
    recomputeTopOffsetOn,
}: PropsWithChildren<DetailsAsideProps>) => {
    const asideRef = useRef<HTMLDivElement | null>(null);
    const { topOffset, hasMeasured } = useDynamicTopOffset({
        containerRef: asideRef,
        recomputeOn: recomputeTopOffsetOn,
    });

    // Bridge the hook's mutable ref through a callback so the ref assignment
    // stays explicit under the current React types.
    const setAsideRef = useCallback((node: HTMLDivElement | null) => {
        asideRef.current = node;
    }, []);

    const style = useMemo(() => {
        if (!hasMeasured) {
            return undefined;
        }
        return {
            minHeight: `calc(100vh - ${topOffset}px)`,
            maxHeight: `calc(100vh - ${topOffset}px)`,
        };
    }, [hasMeasured, topOffset]);

    return (
        <DetailsAsideViewportOffsetContext.Provider
            value={hasMeasured ? topOffset : undefined}
        >
            <Card
                ref={setAsideRef}
                className={classNames(styles.aside, "details-aside", className)}
                sharpEdges
                style={style}
            >
                {children}
            </Card>
        </DetailsAsideViewportOffsetContext.Provider>
    );
};
